'use client'

import { Card } from '@/components/ui/card'
import { getAssetKey, getResolutionByDimensions, getVfxGridVideoPreview } from '@/lib/assets/assets'
import styles from './asset-preview.module.scss'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, buttonVariants } from '@/components/ui/button'
import { ArrowDownToLineIcon, HeartIcon, Images, LoaderCircleIcon } from 'lucide-react'
import { HeartFilledIcon, StarFilledIcon } from '@radix-ui/react-icons'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Skeleton } from '@/components/ui/skeleton'
import { useHover } from '@uidotdev/usehooks'
import { useUserContext } from '@/lib/contexts/UserContext'
import Link from 'next/link'
import useFavorites from '@/lib/hooks/useFavorites'
import { cn, formatBytes } from '@/lib/utils'
import { getFormatPriority, normalizeFormatToken } from '@/lib/assets/format-priority'
import { getResolutionScore, sortResolutionOptions } from '@/lib/assets/resolution-score'
import { features } from '@/lib/config'
import useAsset, { DownloadOption } from '@/lib/hooks/useAsset'
import { AssetSummary } from '@/lib/assets/types'
import isEqual from 'react-fast-compare'
import { toast } from 'sonner'
import { useUserRealtimeContext } from '@/lib/contexts/UserRealtimeContext'
import { useAppStore } from '@/lib/store/store'
//import { Toaster } from '@/components/ui/sonner'

//TODO: Convert TailwindCSS to SCSS

export type AssetPreviewProps = {
  asset: AssetSummary
  className?: string
  onClick?: (asset?: AssetSummary) => void
}

const VideoPreview = memo(
  ({
    src,
    poster,
    show = false,
    hovering = false,
    enableHoverAudio = false,
  }: {
    src?: string
    poster?: string
    show?: boolean
    hovering?: boolean
    enableHoverAudio?: boolean
  }) => {
    const [shown, setShown] = useState(false)
    const [loaded, setLoaded] = useState(false)

    const videoRef = useRef<HTMLVideoElement | null>(null)
    const previousHoveringRef = useRef(false)

    const setVideoRef = useCallback((video: HTMLVideoElement | null) => {
      videoRef.current = video
    }, [])

    useEffect(() => {
      setLoaded(false)
      previousHoveringRef.current = false
    }, [src])

    useEffect(() => {
      if (show && !shown) {
        setShown(true)
      }
    }, [show, shown])

    useEffect(() => {
      const video = videoRef.current

      if (!video || !shown) {
        return
      }

      if (!show) {
        video.pause()
        video.muted = true
        return
      }

      const playPromise = video.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Ignored intentionally.
        })
      }
    }, [show, shown])

    useEffect(() => {
      const video = videoRef.current

      if (!video || !show || !shown) {
        previousHoveringRef.current = hovering
        return
      }

      const isEnteringHover = hovering && !previousHoveringRef.current
      const isLeavingHover = !hovering && previousHoveringRef.current

      if (isEnteringHover) {
        try {
          video.currentTime = 0
        } catch {
          // Ignored intentionally.
        }

        video.muted = !enableHoverAudio

        const playPromise = video.play()
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            video.muted = true
          })
        }
      }

      if (hovering) {
        video.muted = !enableHoverAudio
      }

      if (isLeavingHover || !hovering) {
        video.muted = true
      }

      previousHoveringRef.current = hovering
    }, [enableHoverAudio, hovering, show, shown])

    return (
      <>
        {!!src && shown && (
          <video
            key={src}
            data-slot="video-preview"
            ref={setVideoRef}
            className={cn(styles.videoPreview, 'tw-transition-opacity tw-duration-300', {
              'tw-opacity-0': !loaded || !show,
              'tw-opacity-100': loaded && show,
            })}
            loop
            muted={!enableHoverAudio || !hovering}
            playsInline
            preload="metadata"
            poster={poster ?? ''}
            src={src}
            onLoadedData={() => {
              setLoaded(true)
            }}
          ></video>
        )}
      </>
    )
  },
  isEqual
)

VideoPreview.displayName = 'VideoPreview'

const FALLBACK_FORMAT_KEY = '__no_format__'
const FALLBACK_FORMAT_LABEL = 'Other'
const FALLBACK_RESOLUTION_KEY = '__no_resolution__'
const FALLBACK_RESOLUTION_LABEL = 'Default'
const FALLBACK_CLIPBOARD_IMAGE_TYPE = 'image/png'

const IMAGE_CONTENT_TYPES_BY_EXTENSION: Record<string, string> = {
  avif: 'image/avif',
  bmp: 'image/bmp',
  gif: 'image/gif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
  webp: 'image/webp',
}

const normalizeImageContentType = (value?: string | null) => {
  const normalized = value?.split(';')[0]?.trim().toLowerCase()

  if (!normalized || normalized === 'application/octet-stream' || !normalized.startsWith('image/')) {
    return null
  }

  return normalized
}

const inferImageContentTypeFromUrl = (url: string) => {
  try {
    const pathname = new URL(url, window.location.origin).pathname.toLowerCase()
    const extension = pathname.split('.').pop()
    return extension ? IMAGE_CONTENT_TYPES_BY_EXTENSION[extension] ?? null : null
  } catch {
    return null
  }
}

const isClipboardTypeSupported = (mimeType: string) => {
  if (typeof ClipboardItem === 'undefined') {
    return mimeType === FALLBACK_CLIPBOARD_IMAGE_TYPE
  }

  if (typeof ClipboardItem.supports === 'function') {
    try {
      return ClipboardItem.supports(mimeType)
    } catch {
      return false
    }
  }

  return mimeType === FALLBACK_CLIPBOARD_IMAGE_TYPE
}

const convertImageBlobToPng = async (sourceBlob: Blob) => {
  if (typeof createImageBitmap !== 'function') {
    throw new Error('createImageBitmap is not available')
  }

  const bitmap = await createImageBitmap(sourceBlob)

  try {
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height

    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error('2D context is not available')
    }

    context.drawImage(bitmap, 0, 0)

    const pngBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), FALLBACK_CLIPBOARD_IMAGE_TYPE)
    })

    if (!pngBlob) {
      throw new Error('Failed to convert image to PNG')
    }

    return pngBlob
  } finally {
    bitmap.close()
  }
}

const getResolutionShortLabel = (label: string) => {
  if (!label) {
    return label
  }

  const resolveDimensions = (value?: string | null) => {
    if (!value) {
      return null
    }

    const match = value.match(/(\d+)\s*[x×]\s*(\d+)/i)
    if (!match) {
      return null
    }

    const [, width, height] = match
    const dimensions = `${width}x${height}`.toLowerCase().replace(/×/g, 'x')
    const resolution = getResolutionByDimensions(dimensions)

    return resolution?.name ?? `${width}x${height}`
  }

  const normalized = label.trim()

  const parenMatch = /\(([^)]+)\)/.exec(normalized)
  if (parenMatch?.[1]) {
    const resolved = resolveDimensions(parenMatch[1])
    if (resolved) {
      return resolved
    }
    return parenMatch[1]
  }

  const dimensionsLabel = resolveDimensions(normalized)
  if (dimensionsLabel) {
    return dimensionsLabel
  }

  const [firstToken] = normalized.split(/\s+/)
  const tokenResolved = resolveDimensions(firstToken)
  if (tokenResolved) {
    return tokenResolved
  }

  return firstToken ?? normalized
}

const normalizeFormatPreference = (value?: string | null) => {
  const normalized = normalizeFormatToken(value)

  if (!normalized) {
    return null
  }

  if (normalized === 'seq') {
    return 'pngsequence'
  }

  return normalized
}

const preferenceMatchesToken = (preference: string, token: string) => {
  if (preference === token) {
    return true
  }

  if (preference === 'mp4' || preference === 'h264') {
    return token === 'mp4' || token === 'h264'
  }

  if (preference === 'pngsequence') {
    return token === 'pngsequence' || token === 'seq'
  }

  if (preference.startsWith('prores')) {
    return token.startsWith('prores')
  }

  return token.startsWith(preference) || preference.startsWith(token)
}

const optionMatchesFormatPreference = (option: DownloadOption, preference: string) => {
  const candidateValues = [
    option.format?.value,
    option.format?.label,
    option.variationFormat?.value,
    option.variationFormat?.label,
    option.file.properties?.variations?.format,
    option.file.properties?.format,
    option.file.properties?.extension,
  ]

  return candidateValues
    .map((value) => normalizeFormatToken(value))
    .filter((value): value is string => !!value)
    .some((token) => preferenceMatchesToken(preference, token))
}

const getDownloadOptionResolutionScore = (option: DownloadOption) => {
  const sources: Array<string | undefined> = [
    option.resolution?.value,
    option.resolution?.label,
    option.file.properties?.variations &&
    'resolution' in option.file.properties?.variations &&
    typeof option.file.properties?.variations?.resolution === 'string'
      ? option.file.properties?.variations?.resolution
      : undefined,
  ]

  let bestScore = 0

  for (const source of sources) {
    bestScore = Math.max(bestScore, getResolutionScore(source))
  }

  return bestScore
}

const AssetPreview = ({ asset, className, onClick = () => {} }: AssetPreviewProps) => {
  const assetKey = getAssetKey(asset)
  const { addFavorite, removeFavorite, isFavorite: isFavoriteFn } = useFavorites()
  const isFavorite = isFavoriteFn(assetKey)
  const useVfxVideoGridPreview = useAppStore((state) => state.useVfxVideoGridPreview)
  const vfxGridPreviewPreferredVariant = useAppStore((state) => state.vfxGridPreviewPreferredVariant)
  const enableVfxPreviewSoundOnHover = useAppStore((state) => state.enableVfxPreviewSoundOnHover)

  const imgRef = useRef<HTMLImageElement>(null)

  const [ref, hoveringCard] = useHover()
  const { user, userToken, isAuthenticating } = useUserContext()

  const { isLightBackground, thumbnail, videoPreview, is4K, assetDetailUrl, downloadOptions, download, isDownloading } =
    useAsset({ asset, userToken })

  const [highlightedOption, setHighlightedOption] = useState<DownloadOption | null>(null)
  const [selectedOption, setSelectedOption] = useState<DownloadOption | null>(null)
  const [isHoveringDownloadOptions, setIsHoveringDownloadOptions] = useState(false)

  const hovering = hoveringCard || isHoveringDownloadOptions || isDownloading
  const vfxEnhancedPreviewsEnabled = features.vfxEnhancedPreviews
  const preferredVfxGridVideoPreview = useMemo(
    () => (vfxEnhancedPreviewsEnabled ? getVfxGridVideoPreview(asset, vfxGridPreviewPreferredVariant) : null),
    [asset, vfxEnhancedPreviewsEnabled, vfxGridPreviewPreferredVariant]
  )
  const shouldUseVfxGridVideoPreview =
    vfxEnhancedPreviewsEnabled && useVfxVideoGridPreview && !!preferredVfxGridVideoPreview
  const defaultVideoPreview = shouldUseVfxGridVideoPreview ? preferredVfxGridVideoPreview : null
  const shouldEnableHoverAudio =
    vfxEnhancedPreviewsEnabled && asset.vertical === 'videos' && enableVfxPreviewSoundOnHover

  const [matrixHovered, setMatrixHovered] = useState(false)

  const isHighlightedPremium = useMemo(() => {
    if (asset.level > 0) return true
    if (!highlightedOption) return false
    const level =
      typeof highlightedOption.level === 'number' ? highlightedOption.level : Number(highlightedOption.level)
    return Number.isFinite(level) && level > 0
  }, [asset.level, highlightedOption])

  const userVideoFormatPreference = useMemo(
    () => normalizeFormatPreference(user?.settings?.videoFormat),
    [user?.settings?.videoFormat]
  )

  //Just a flag to show/hide the hover gradient at the bottom
  const showGradient = false
  //console.log('userVideoFormatPreference', userVideoFormatPreference, user?.settings)

  const preferredDownloadOption = useMemo(() => {
    if (!downloadOptions.length || !userVideoFormatPreference) {
      return null
    }

    const matchingOptions = downloadOptions.filter((option) =>
      optionMatchesFormatPreference(option, userVideoFormatPreference)
    )

    if (!matchingOptions.length) {
      return null
    }

    let bestOption: DownloadOption | null = null
    let bestScore = 0

    for (const option of matchingOptions) {
      const score = getDownloadOptionResolutionScore(option)
      if (!bestOption || score > bestScore) {
        bestOption = option
        bestScore = score
      }
    }

    return bestOption
  }, [downloadOptions, userVideoFormatPreference])

  const defaultDownloadOption = preferredDownloadOption ?? downloadOptions[0] ?? null

  // Determine if portal download is possible
  const { state: userStatus, download_method: downloadMethod } = useUserRealtimeContext()
  const isPortalDownload = userStatus === 'online' && downloadMethod === 'portal'

  // When downloading state change reste hover
  useEffect(() => {
    if (isDownloading) {
      setIsHoveringDownloadOptions(false)
    }
  }, [isDownloading])

  const copyImgToClipboard = async (url: string) => {
    try {
      console.debug('Copying image to clipboard:', url)
      const toastId = toast.info('Copying...')
      const proxyUrl = `/api/image-proxy?url=${encodeURIComponent(url)}`
      const res = await fetch(proxyUrl)
      if (!res.ok) {
        throw new Error(`Image proxy failed with status ${res.status}`)
      }

      const rawBlob = await res.blob()
      const normalizedContentType =
        normalizeImageContentType(rawBlob.type) ?? inferImageContentTypeFromUrl(url) ?? FALLBACK_CLIPBOARD_IMAGE_TYPE

      let clipboardBlob = rawBlob
      let clipboardType = normalizedContentType

      if (!isClipboardTypeSupported(clipboardType)) {
        clipboardBlob = await convertImageBlobToPng(rawBlob)
        clipboardType = FALLBACK_CLIPBOARD_IMAGE_TYPE
      }

      if (clipboardBlob.type !== clipboardType) {
        clipboardBlob = new Blob([clipboardBlob], { type: clipboardType })
      }

      const item = new ClipboardItem({ [clipboardType]: clipboardBlob })
      await navigator.clipboard.write([item])

      toast.dismiss(toastId)
      toast.success('Copied to clipboard!')
    } catch (e) {
      console.error('Failed to copy image', e)
      toast.warning('Error copying to clipboard')
    }
  }

  const handleDownloadOptionsOpenChange = useCallback(
    (open: boolean) => {
      setIsHoveringDownloadOptions(open)

      const fallbackOption = selectedOption ?? defaultDownloadOption ?? null

      if (open) {
        setHighlightedOption((current) => current ?? fallbackOption)
        return
      }

      setHighlightedOption(fallbackOption)
    },
    [defaultDownloadOption, selectedOption]
  )

  const handleDownload = useCallback(
    (option: DownloadOption) => {
      if (isDownloading) {
        console.warn('Download already in progress')
        return
      }
      download(option.file, {
        platform: isPortalDownload ? 'portal' : 'web',
      })
    },
    [download, isDownloading, isPortalDownload]
  )

  const downloadMatrix = useMemo(() => {
    if (!downloadOptions || downloadOptions.length === 0) {
      return {
        formats: [] as Array<{
          key: string
          label: string
          optionsByResolution: Map<string, DownloadOption>
        }>,
        resolutions: [] as Array<{ key: string; label: string }>,
      }
    }

    const resolutionKeys = new Set<string>()
    const resolutions: Array<{ key: string; label: string }> = []
    const formatMap = new Map<
      string,
      {
        key: string
        label: string
        optionsByResolution: Map<string, DownloadOption>
      }
    >()
    const formatOrder: Array<{
      key: string
      label: string
      optionsByResolution: Map<string, DownloadOption>
    }> = []

    downloadOptions.forEach((option) => {
      const formatKey = option.format?.value ?? option.format?.label ?? FALLBACK_FORMAT_KEY
      const formatLabel = option.format?.label ?? option.format?.value ?? FALLBACK_FORMAT_LABEL

      let formatEntry = formatMap.get(formatKey)
      if (!formatEntry) {
        formatEntry = {
          key: formatKey,
          label: formatLabel,
          optionsByResolution: new Map(),
        }
        formatMap.set(formatKey, formatEntry)
        formatOrder.push(formatEntry)
      }

      const resolutionKey = option.resolution?.value ?? option.resolution?.label ?? FALLBACK_RESOLUTION_KEY
      const resolutionLabel = option.resolution?.label ?? option.resolution?.value ?? FALLBACK_RESOLUTION_LABEL

      if (!resolutionKeys.has(resolutionKey)) {
        resolutionKeys.add(resolutionKey)
        resolutions.push({ key: resolutionKey, label: resolutionLabel })
      }

      formatEntry.optionsByResolution.set(resolutionKey, option)
    })

    const prioritizedFormats = formatOrder
      .map((entry, index) => ({ entry, index }))
      .sort((a, b) => {
        const priorityDiff =
          getFormatPriority({ value: a.entry.key, label: a.entry.label }) -
          getFormatPriority({ value: b.entry.key, label: b.entry.label })

        if (priorityDiff !== 0) {
          return priorityDiff
        }

        return a.index - b.index
      })
      .map(({ entry }) => entry)

    return {
      formats: prioritizedFormats,
      resolutions: sortResolutionOptions(resolutions),
    }
  }, [downloadOptions])

  useEffect(() => {
    if (!downloadOptions.length) {
      setSelectedOption(null)
      setHighlightedOption(null)
      return
    }

    const fallbackOption = defaultDownloadOption ?? downloadOptions[0] ?? null

    setSelectedOption((current) => {
      if (current) {
        const stillAvailable = downloadOptions.find((option) => option.file.uuid === current.file.uuid)
        if (stillAvailable) {
          return stillAvailable
        }
      }

      return fallbackOption
    })
  }, [asset.uuid, defaultDownloadOption, downloadOptions])

  const baseOption = selectedOption ?? defaultDownloadOption

  useEffect(() => {
    if (isHoveringDownloadOptions) {
      return
    }

    if (!baseOption) {
      if (highlightedOption) {
        setHighlightedOption(null)
      }
      return
    }

    if (!highlightedOption || highlightedOption.file.uuid !== baseOption.file.uuid) {
      setHighlightedOption(baseOption)
    }
  }, [baseOption, highlightedOption, isHoveringDownloadOptions])

  const displayedOption = highlightedOption ?? baseOption
  const displayedFileUuid = displayedOption?.file.uuid

  const displayedSize = useMemo(() => {
    if (!displayedOption) {
      return '—'
    }
    const size = displayedOption.size ?? displayedOption.file.properties?.size
    if (typeof size === 'number' && size > 0) {
      return formatBytes(size)
    }
    return '—'
  }, [displayedOption])

  const displayedResolution = useMemo(() => {
    if (!displayedOption) {
      return '—'
    }
    const direct = displayedOption.resolution?.value ?? displayedOption.resolution?.label
    if (direct) {
      return direct
    }
    const variations = displayedOption.file.properties?.variations as { resolution?: string } | undefined
    return variations?.resolution ?? '—'
  }, [displayedOption])

  return (
    <Card
      data-slot="asset-preview"
      className={cn(
        'asset-preview',
        styles.card,
        '!tw-max-w-full',
        {
          '!tw-bg-[var(--light-assets-background-color)]': isLightBackground,
        },
        className
      )}
      onClick={onClick}
      ref={ref}
    >
      {/*<div className={styles.image} style={{*/}
      {/*  backgroundImage: `url(${asset.image ?? ''})`,*/}
      {/*  backgroundRepeat: 'no-repeat',*/}
      {/*  backgroundSize: 'cover',*/}
      {/*  backgroundPosition: 'center',*/}
      {/*}}></div>*/}
      <HoverCard openDelay={0} closeDelay={100} onOpenChange={handleDownloadOptionsOpenChange}>
        <HoverCardTrigger asChild>
          <div
            className={cn(styles.body, {
              [styles.bodyHover]: hovering,
              [styles.bodyHoverGradient]: hovering && showGradient,
            })}
          >
            {!!thumbnail && (
              <picture className={styles.picture}>
                <source srcSet={thumbnail ?? ''} type="image/png" />
                <img className={styles.image} src={thumbnail ?? ''} alt={asset.name || 'Asset preview'} ref={imgRef} />
              </picture>
            )}

            {/* Default VFX video thumbnail */}
            <VideoPreview
              src={defaultVideoPreview ?? undefined}
              poster={thumbnail ?? undefined}
              show={!!defaultVideoPreview}
              hovering={hovering}
              enableHoverAudio={shouldEnableHoverAudio}
            />

            {/* Hover video preview (legacy behavior fallback) */}
            <VideoPreview
              src={videoPreview ?? undefined}
              poster={thumbnail ?? undefined}
              show={!defaultVideoPreview && hovering && !!videoPreview}
              hovering={hovering}
              enableHoverAudio={shouldEnableHoverAudio}
            />
            <div
              className={cn(styles.content, {
                [styles.contentHover]: hovering,
              })}
            >
              <div className="tw-absolute tw-left-0 tw-top-1 tw-flex tw-w-full tw-flex-row tw-items-start tw-px-2">
                {isHighlightedPremium && (
                  <span className={styles.premiumIcon} title="Premium asset">
                    <StarFilledIcon className={styles.starIcon} />
                  </span>
                )}
                <Link
                  className="hover:tw-underline"
                  href={assetDetailUrl ?? '#'}
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  {asset.name.replace('(4K)', '').replace('4K', '')}
                </Link>
                {is4K && (
                  <span
                    className={cn('body_4 tw-ml-auto tw-rounded tw-border tw-border-[var(--Primary-100)] tw-px-1', {
                      '!tw-border-black': isLightBackground,
                      '!tw-text-black': isLightBackground,
                    })}
                  >
                    4K
                  </span>
                )}
              </div>
              <HoverCardTrigger asChild style={{ height: '100%' }}>
                {/* Overlay */}
                <div className="tw-absolute tw-bottom-0 tw-flex tw-w-full tw-flex-row tw-items-center tw-space-x-2 tw-p-4">
                  <div className="tw-mt-auto tw-flex tw-w-full tw-flex-row tw-items-center tw-space-x-3">
                    {/* Favorite button */}
                    {features.favorites && (
                      <Button
                        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                        variant="transparent"
                        size="icon"
                        className="body_4 tw-flex-1"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          isFavorite ? removeFavorite(assetKey) : addFavorite(assetKey)
                        }}
                      >
                        {isFavorite ? (
                          <HeartFilledIcon className="tw-h-4 tw-w-4" />
                        ) : (
                          <HeartIcon className="tw-h-4 tw-w-4" />
                        )}
                      </Button>
                    )}
                    {!!thumbnail && (videoPreview || defaultVideoPreview) && (
                      <Button
                        title={'Copy preview pic to clipboard'}
                        variant="transparent"
                        size="icon"
                        className="tw-mr-auto !tw-p-1"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          copyImgToClipboard(thumbnail ?? '')
                        }}
                      >
                        <Images className="tw-h-4 tw-w-4" />
                      </Button>
                    )}
                    {/* variant info */}
                    <div className="tw-ml-auto tw-flex tw-flex-col tw-items-end tw-text-right tw-leading-tight">
                      <span className="tw-text-xs tw-font-semibold tw-text-white/70">{displayedSize}</span>
                      <span className="tw-text-[10px] tw-text-white/70">{displayedResolution}</span>
                    </div>
                    {/* Download button */}
                    {isDownloading && <LoaderCircleIcon className="tw-h-4 tw-w-4 tw-animate-spin" />}
                    {!isDownloading && (
                      <Link
                        href={baseOption?.url ?? '#'}
                        title="Download"
                        className={cn(
                          buttonVariants({
                            variant: 'transparent',
                            size: 'icon',
                          }),
                          '!tw-p-1'
                        )}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()

                          const optionToDownload = baseOption ?? defaultDownloadOption

                          if (!optionToDownload) {
                            console.warn('No download option selected')
                            return
                          }

                          setSelectedOption(optionToDownload)
                          setHighlightedOption(optionToDownload)
                          handleDownload(optionToDownload)
                        }}
                      >
                        {!isDownloading && <ArrowDownToLineIcon className="tw-h-4 tw-w-4" />}
                      </Link>
                    )}
                  </div>
                </div>
              </HoverCardTrigger>

              <HoverCardContent
                className="tw-rounded-[7px] tw-border-0 !tw-bg-[var(--Primary)] tw-animate-in tw-fade-in"
                style={{ width: 'var(--radix-hover-card-trigger-width)' }}
                side="bottom"
                sideOffset={0}
                align="start"
                alignOffset={0}
              >
                <div
                  className="tw-flex tw-w-full tw-flex-col tw-space-y-2"
                  onMouseLeave={() => {
                    const fallbackOption = selectedOption ?? defaultDownloadOption ?? null
                    setHighlightedOption(fallbackOption)
                    setMatrixHovered(false)
                  }}
                  onMouseEnter={() => setMatrixHovered(true)}
                >
                  {isAuthenticating ? (
                    <>
                      <Skeleton className="tw-h-4 tw-w-16" />
                      <Skeleton className="tw-h-4 tw-w-16" />
                      <Skeleton className="tw-h-4 tw-w-16" />
                    </>
                  ) : downloadMatrix.formats.length === 0 ? (
                    <span className="tw-text-xs tw-font-medium tw-uppercase tw-text-white/60">
                      No download options available
                    </span>
                  ) : (
                    downloadMatrix.formats.map((row) => {
                      const isActiveRow = Array.from(row.optionsByResolution.values()).some(
                        (option) => option.file.uuid === displayedFileUuid
                      )

                      return (
                        <div
                          key={`download-row-${row.key}`}
                          className={cn(
                            'tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-white/60 tw-transition-colors tw-duration-150 hover:tw-text-white',
                            {
                              'tw-text-white': isActiveRow,
                            }
                          )}
                        >
                          <span
                            className={cn('tw-min-w-[45px] tw-text-right tw-text-xs tw-font-semibold tw-uppercase', {
                              'tw-text-white': isActiveRow,
                            })}
                            style={{ border: '0px solid red' }}
                          >
                            {row.label.replace('SEQUENCE', 'SEQ.')}
                          </span>
                          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1">
                            {downloadMatrix.resolutions.map((resolution) => {
                              const option = row.optionsByResolution.get(resolution.key)
                              if (!option) {
                                return null
                              }

                              const isActiveOption = option.file.uuid === displayedFileUuid
                              const label = option.resolution?.label ?? resolution.label
                              const shortLabel = getResolutionShortLabel(label)

                              return (
                                <Link
                                  key={`download-cell-${row.key}-${resolution.key}`}
                                  href={option.url}
                                  prefetch={false}
                                  className={cn(
                                    'tw-rounded tw-border tw-border-white/10 tw-px-3 tw-py-0 tw-text-xs tw-uppercase tw-text-white/60 tw-transition-colors tw-duration-150 hover:tw-border-[var(--Primary-100)] hover:tw-bg-[var(--Primary-200)] hover:tw-text-black',
                                    {
                                      '!tw-border-white !tw-text-white':
                                        !matrixHovered &&
                                        shortLabel == displayedResolution &&
                                        displayedFileUuid === option.file.uuid,
                                    }
                                  )}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setSelectedOption(option)
                                    setHighlightedOption(option)
                                    handleDownload(option)
                                  }}
                                  onMouseEnter={() => setHighlightedOption(option)}
                                  onFocus={() => setHighlightedOption(option)}
                                  onMouseLeave={() => setHighlightedOption(baseOption ?? option)}
                                  onBlur={() => setHighlightedOption(baseOption ?? option)}
                                >
                                  {shortLabel}
                                </Link>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </HoverCardContent>
            </div>
          </div>
        </HoverCardTrigger>
      </HoverCard>
    </Card>
  )
}

export { AssetPreview }
